import {HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse} from "@angular/common/http";
import { Injectable } from "@angular/core";
import {Observable, switchMap, take, tap} from "rxjs";
import { AUTH_API, RefreshQueueService } from "../services/auth/auth.service";
import { JwtService } from "../services/jwt/jwt.service";
import {CentralConfigsService} from "../../public/authentication/services/centralConfigs.service";


@Injectable({
    providedIn: 'root'
})
export class JWTInterceptor implements HttpInterceptor {
    private domain: string = '';
    appToken: any = null;

    constructor(
        private refreshQueueService: RefreshQueueService, 
        private centralConfigsService: CentralConfigsService, 
        private jwtService: JwtService
    ) {
        this.appToken = this.jwtService.getToken();
        // Subscribe to domain changes
        this.centralConfigsService._domain.subscribe(domain => {
            this.domain = domain;
        });
    }

    public intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        if (!request.url.startsWith('http') && !request.url.includes('assets')) {
            const currentDomain = this.centralConfigsService.getEnvironment();
            if (currentDomain) {
                this.domain = currentDomain;
            }

            request = request.clone({
                url: `${this.domain}${request.url}`,
                setHeaders: {
                    'X-Requested-With': 'XMLHttpRequest',
                },
            });
        }

        if (this.isApiUrl(request.url)) {
            return this.handleRequest(request, next).pipe(
                tap(event => {
                    if (event instanceof HttpResponse) {
                        const refreshHeader = event.headers.get('Refresh');
                        if (refreshHeader && refreshHeader === 'Token') {
                            this.refreshQueueService.pushRefresh();
                        }
                    }
                })
            )
        }
        return next.handle(request);
    }

    private isApiUrl(url: string): boolean {
        const isTokenLoginUrl: boolean = url.endsWith(AUTH_API.Login);
        const isTokenRefreshUrl: boolean = url.includes(AUTH_API.Refresh(''));
        const isSignalRUrl: boolean = url.startsWith("/hub/");
        return !isTokenLoginUrl && !isTokenRefreshUrl && !isSignalRUrl;
    }
    public handleRequest(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        const tokenPair = this.jwtService.getToken();
        if (!tokenPair) {
            return next.handle(request);
        }
        const isAccessTokenExpired = this.jwtService.isTokenExpired(tokenPair.accessToken);
        if (!isAccessTokenExpired) {
            return this.addTokenToRequest(tokenPair.accessToken, request, next);
        }

        const isRefreshTokenExpired = this.jwtService.isTokenExpired(tokenPair.refreshToken);
        if (isRefreshTokenExpired) {
             this.refreshQueueService.logoutQueue.next();
            return next.handle(request);
        }
        this.refreshQueueService.pushRefresh();
        return this.refreshQueueService.refreshToken$.pipe(
            take(1),
            switchMap(result => {
                return this.addTokenToRequest(result.value.tokenPair?.accessToken ?? '', request, next);
            }),
        );
    }

    private addTokenToRequest(accessToken: string, request: HttpRequest<unknown>, next: HttpHandler) {
        request = request.clone({ headers: request.headers.append("Authorization", `Bearer ${accessToken}`) });
        return next.handle(request);
    }

}
